import { useEffect, useState, useMemo, Fragment, useCallback, useRef } from "react";
import { useNavGuard } from "@/contexts/NavigationContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtMon, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { PlusIcon, TrashIcon, ArrowLeftIcon, BuildingsIcon, WarningCircleIcon, CheckCircleIcon, TruckIcon, GlobeIcon, PackageIcon, PlantIcon } from "@phosphor-icons/react";
import SupplierSearch from "@/components/SupplierSearch";
import ProductSearch from "@/components/ProductSearch";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";

// Por defecto «solo para el costo»: el gasto se reparte al costo real sin proveedor ni contabilidad (se carga después)
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Reparto exacto en centavos (residuo mayor), igual que services/prorrateo.prorratear del servidor
const prorratear = (bases, total) => {
  const cents = Math.round(total * 100);
  const suma = bases.reduce((a, b) => a + b, 0);
  if (!(suma > 0)) return bases.map(() => 0);
  const raw = bases.map((b) => (cents * b) / suma);
  const out = raw.map((x) => Math.floor(x));
  let resto = cents - out.reduce((a, b) => a + b, 0);
  const orden = raw.map((x, i) => [x - out[i], i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (let k = 0; resto > 0; k += 1, resto -= 1) out[orden[k % orden.length][1]] += 1;
  return out.map((c) => c / 100);
};
const BASE_TXT = { valor: "Valor de compra de la línea", cantidad: "Cantidad", peso: "Peso (kg)" };

const nuevoGasto = () => ({ concepto: "", monto: "", moneda: "HNL", tipo_cambio: "1", isv_monto: "", incorporable: true, cuenta_gasto: "",
  forma_pago: "solo_costo", proveedor_id: "", numero_documento: "", cuenta_pago_id: "" });

/**
 * Factura de proveedor (y orden de compra). La factura se registra en un solo flujo: factura → capas PEPS con su
 * costo → CxP del proveedor en la sucursal activa (auxiliar) o pago → asiento; flete y gastos de importación se
 * distribuyen al costo según la base elegida (vista previa antes de confirmar).
 * Moneda: lempiras o dólares (con la tasa en lempiras por dólar); el resto del sistema recibe el equivalente en lempiras.
 * Ruta /compras/factura/:id/editar: edición de una factura existente, solo para el Administrador General (con motivo).
 */
export default function PurchaseNew({ mode = "invoice" }) {
  const navigate = useNavigate();
  const { currentBranch } = useCompany() || {};
  const [searchParams] = useSearchParams();
  const poId = mode === "invoice" ? searchParams.get("po_id") : null;
  const { id: editId } = useParams();
  const editando = mode === "invoice" && Boolean(editId);
  const { user } = useAuth() || {};
  const esAdmin = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const [original, setOriginal] = useState(null);     // factura que se edita
  const [motivo, setMotivo] = useState("");
  const [moneda, setMoneda] = useState("HNL");
  const [tasa, setTasa] = useState("");
  // Subcuenta del proveedor por país de procedencia (proveedor → subcuenta → auxiliar por sucursal)
  const [subcuentas, setSubcuentas] = useState(null);       // null = sin cargar
  const [subcuentaId, setSubcuentaId] = useState("");
  const [nuevaSub, setNuevaSub] = useState(null);           // {pais, cuenta} mientras se agrega una subcuenta
  const [paises, setPaises] = useState([]);
  const [cuentasCxp, setCuentasCxp] = useState([]);
  const [creandoSub, setCreandoSub] = useState(false);
  const [po, setPo] = useState(null);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [diasCredito, setDiasCredito] = useState("");
  const [treasury, setTreasury] = useState([]);
  const [cuentaPago, setCuentaPago] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [provEstado, setProvEstado] = useState("cargando");   // cargando | ok | error (≠ búsqueda sin coincidencias)
  const [products, setProducts] = useState([]);
  const [proveedorId, setProveedorId] = useState("");
  const [numeroProv, setNumeroProv] = useState("");
  const [caiProv, setCaiProv] = useState("");
  const [formaPago, setFormaPago] = useState("credito");
  const [fechaVenc, setFechaVenc] = useState("");
  const [fechaEntrega, setFechaEntrega] = useState("");
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);
  // Integración: tipo de compra (obligatorio), auxiliar del proveedor en la sucursal, flete, gastos y prorrateo
  const [tipoCompra, setTipoCompra] = useState("");
  const [aux, setAux] = useState(null);               // {auxiliar, cuenta_sugerida, cuentas_aplicables, sucursal} | {error}
  const [cuentaAux, setCuentaAux] = useState("");
  const [creandoAux, setCreandoAux] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [fleteTrat, setFleteTrat] = useState("");
  const [base, setBase] = useState("");
  const [gastos, setGastos] = useState([]);
  const [expediente, setExpediente] = useState("");
  const [cuentasGasto, setCuentasGasto] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [idemKey] = useState(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`));
  const qtyRefs = useRef({});
  // Flechas del ERP: la compra solo se registra con su propio botón (no desde el aviso de navegación)
  const hayDatos = useCallback(() => !saving && (items.length > 0 || Boolean(proveedorId)), [saving, items, proveedorId]);
  useNavGuard("purchase-new", {
    isDirty: hayDatos, sinGuardar: "El documento se registra con su botón después de revisarlo; descarta o cancela.",
  });

  // Catálogo de proveedores activos: se carga aparte para que un fallo se informe (y se reintente) sin bloquear el resto
  const cargarProveedores = useCallback(async () => {
    setProvEstado("cargando");
    try {
      const { data } = await api.get("/suppliers");
      setSuppliers(Array.isArray(data) ? data : []);
      setProvEstado("ok");
    } catch { setProvEstado("error"); }
  }, []);
  useEffect(() => { cargarProveedores(); }, [cargarProveedores]);

  useEffect(() => {
    (async () => {
      const [p, w, t, c, cg] = await Promise.all([
        api.get("/products"),
        api.get("/warehouses", { params: { activo: true } }).catch(() => ({ data: [] })),
        api.get("/treasury/accounts").catch(() => ({ data: [] })),
        api.get("/purchases/accounting-config").catch(() => ({ data: null })),
        api.get("/purchases/cuentas-aplicables", { params: { uso: "gasto" } }).catch(() => ({ data: [] })),
      ]);
      setProducts((p.data || []).filter((x) => x.activo !== false)); setTreasury(t.data || []);
      setCfg(c.data); setCuentasGasto(cg.data || []);
      if (c.data) { setFleteTrat(c.data.flete_tratamiento || "costo"); setBase(c.data.base_prorrateo || "valor"); }
      if (poId) {
        try {
          const { data: oc } = await api.get(`/purchase-orders/${poId}`);
          setPo(oc);
          setProveedorId(oc.proveedor_id);
          setFormaPago(oc.forma_pago || "credito");
          if (oc.dias_credito !== null && oc.dias_credito !== undefined) setDiasCredito(String(oc.dias_credito));
          const pend = (oc.items || []).map((it) => ({
            ...it,
            cantidad: Math.round((Number(it.cantidad_recibida || 0) - Number(it.cantidad_facturada || 0)) * 10000) / 10000,
            descuento: 0, es_semilla: false,
          })).filter((it) => it.cantidad > 0);
          setItems(pend);
          if (!pend.length) toast.info("La OC no tiene mercadería recibida pendiente de facturar");
        } catch (e) { toast.error(apiErr(e)); }
      }
      setWarehouses((w.data || []).filter((x) => x.tipo !== "transito"));
      if (editId && mode === "invoice") {
        try {
          const { data: f } = await api.get(`/purchase-invoices/${editId}`);
          const d10 = (v) => (v ? String(v).slice(0, 10) : "");
          setOriginal(f);
          setProveedorId(f.proveedor_id); setNumeroProv(f.numero_proveedor || ""); setCaiProv(f.cai_proveedor || "");
          setFormaPago(f.forma_pago || "credito"); setFecha(d10(f.fecha)); setFechaVenc(d10(f.fecha_vencimiento));
          setDiasCredito(f.dias_credito !== null && f.dias_credito !== undefined ? String(f.dias_credito) : "");
          setNotas(f.notas || ""); setTipoCompra(f.tipo_compra || "nacional"); setExpediente(f.expediente || "");
          if (f.flete_tratamiento) setFleteTrat(f.flete_tratamiento);
          if (f.base_prorrateo) setBase(f.base_prorrateo);
          setMoneda(f.moneda || "HNL"); setTasa(f.moneda === "USD" ? String(f.tipo_cambio) : "");
          setSubcuentaId(f.subcuenta_id || "");
          setItems((f.items || []).map((it) => ({
            ...it, precio_unitario: it.precio_unitario_moneda ?? it.precio_unitario, descuento: it.descuento_moneda ?? it.descuento,
            peso_kg: it.peso_kg || "", fecha_fabricacion: d10(it.fecha_fabricacion), fecha_vencimiento: d10(it.fecha_vencimiento),
            fecha_test: d10(it.fecha_test), fecha_ingreso: d10(it.fecha_ingreso), bodega_id: it.bodega_id || "",
          })));
          setGastos((f.gastos || []).map((g) => ({
            concepto: g.concepto, monto: String(g.monto), moneda: g.moneda || "HNL", tipo_cambio: String(g.tipo_cambio || 1),
            isv_monto: g.isv_monto ? String(g.isv_monto) : "", incorporable: g.incorporable !== false, cuenta_gasto: g.cuenta_gasto || "",
            forma_pago: g.forma_pago || "credito", proveedor_id: g.proveedor_id || "", numero_documento: g.numero_documento || "",
            cuenta_pago_id: g.cuenta_pago_id || "",
          })));
        } catch (e) { toast.error(apiErr(e)); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subcuentas del proveedor: si tiene, la compra debe indicar la suya
  const cargarSubcuentas = useCallback(async () => {
    if (mode !== "invoice" || !proveedorId) { setSubcuentas(null); return []; }
    try {
      const { data } = await api.get(`/suppliers/${proveedorId}/subcuentas`);
      const lista = (data.subcuentas || []).filter((x) => x.activo !== false);
      setSubcuentas(lista);
      setSubcuentaId((prev) => (lista.some((x) => x.id === prev) ? prev : (lista.length === 1 ? lista[0].id : "")));
      return lista;
    } catch { setSubcuentas([]); return []; }
  }, [mode, proveedorId]);
  useEffect(() => { cargarSubcuentas(); setNuevaSub(null); }, [cargarSubcuentas]);
  const requiereSub = (subcuentas || []).length > 0;
  const subSel = (subcuentas || []).find((x) => x.id === subcuentaId);
  const abrirNuevaSub = async () => {
    setNuevaSub({ pais: "", cuenta: "" });
    if (!paises.length) {
      const [pa, cx] = await Promise.all([api.get("/paises-procedencia"), api.get("/purchases/cuentas-aplicables", { params: { uso: "cxp" } })]);
      setPaises(pa.data || []); setCuentasCxp(cx.data || []);
    }
  };
  const subDelPais = (pais) => (subcuentas || []).find((x) => x.pais_codigo === pais);
  const crearSubcuenta = async () => {
    if (!nuevaSub?.pais) return toast.error("Selecciona el país de procedencia");
    const existente = subDelPais(nuevaSub.pais);
    if (existente) { setSubcuentaId(existente.id); setNuevaSub(null); return toast.info(`Seleccionada la subcuenta existente ${existente.nombre}`); }
    setCreandoSub(true);
    try {
      const { data } = await api.post(`/suppliers/${proveedorId}/subcuentas`, { pais_codigo: nuevaSub.pais, cuenta_control: nuevaSub.cuenta || "" });
      await cargarSubcuentas();
      setSubcuentaId(data.subcuenta.id);
      setNuevaSub(null);
      toast.success(`Subcuenta ${data.subcuenta.nombre} (${data.subcuenta.codigo}) creada${data.auxiliar ? ` con su auxiliar ${data.auxiliar.codigo}` : ""}`);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.subcuenta) {
        await cargarSubcuentas(); setSubcuentaId(det.subcuenta.id); setNuevaSub(null);
        toast.info(det.mensaje || "La subcuenta ya existía: quedó seleccionada");
      } else toast.error(apiErr(e));
    } finally { setCreandoSub(false); }
  };

  // Auxiliar contable del proveedor (y su subcuenta) en la sucursal activa (se asigna solo; si falta, se crea aquí mismo)
  const cargarAux = useCallback(async () => {
    if (mode !== "invoice" || !proveedorId) { setAux(null); return; }
    if (subcuentas === null || (subcuentas.length > 0 && !subcuentaId)) { setAux(null); return; }
    try {
      const { data } = await api.get(`/suppliers/${proveedorId}/auxiliar`, { params: { tipo_compra: tipoCompra || "nacional", subcuenta_id: subcuentaId || "" } });
      setAux(data);
      setCuentaAux((prev) => (data.auxiliar ? data.auxiliar.cuenta_control : (prev && data.cuentas_aplicables.some((c) => c.codigo === prev) ? prev : data.cuenta_sugerida)));
    } catch (e) { setAux({ error: apiErr(e) }); }
  }, [mode, proveedorId, tipoCompra, subcuentas, subcuentaId]);
  useEffect(() => { cargarAux(); }, [cargarAux, currentBranch?.id]);
  const crearAux = async () => {
    setCreandoAux(true);
    try {
      await api.post(`/suppliers/${proveedorId}/auxiliar`, { cuenta_control: cuentaAux, subcuenta_id: subcuentaId || "" });
      toast.success("Auxiliar creado para esta sucursal");
      await cargarAux();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCreandoAux(false); }
  };

  const proveedor = suppliers.find((s) => s.id === proveedorId);
  // Días de crédito: los del documento o, por defecto, los del proveedor
  const diasEfectivos = diasCredito !== "" ? Number(diasCredito) : (proveedor ? Number(proveedor.dias_credito || 0) : 0);
  const vencCalculado = useMemo(() => {
    if (formaPago !== "credito" || !fecha) return "";
    const d = new Date(`${fecha}T12:00:00`);
    d.setDate(d.getDate() + (diasEfectivos || 0));
    return d.toISOString().slice(0, 10);
  }, [fecha, diasEfectivos, formaPago]);
  const principal = warehouses.find((w) => w.tipo === "principal");
  const prodDe = (id) => products.find((p) => p.id === id);
  const esServicio = (it) => { const p = prodDe(it.producto_id); return Boolean(p && (p.tipo === "servicio" || p.es_flete)); };
  const hayFlete = items.some((it) => prodDe(it.producto_id)?.es_flete);

  const addRow = () => setItems([...items, {
    producto_id: "", codigo: "", descripcion: "",
    cantidad: 1, precio_unitario: 0, descuento: 0, isv_pct: 15, peso_kg: "",
    batch: "", lote_fabricacion: "", fecha_fabricacion: "", fecha_vencimiento: "", fecha_test: "",
    fecha_ingreso: new Date().toISOString().slice(0, 10),
    bodega_id: principal ? principal.id : "", es_semilla: false,
  }]);
  const updateRow = (i, patch) => setItems((prev) => { const n = [...prev]; n[i] = { ...n[i], ...patch }; return n; });
  const pickProduct = (i, pid) => {
    const p = prodDe(pid);
    if (!p) return;
    updateRow(i, {
      producto_id: p.id, codigo: p.codigo,
      descripcion: p.es_flete ? "Flete" : (p.nombre || p.descripcion || `${p.categoria} - ${p.sku || p.codigo}`),
      precio_unitario: p.ultimo_costo || p.costo_promedio || 0, isv_pct: p.isv ?? 15,
      es_semilla: !!p.es_semilla,
    });
    // después de elegir: directo a la cantidad
    setTimeout(() => { const el = qtyRefs.current[i]; if (el) { el.focus(); el.select(); } }, 60);
  };
  const removeRow = (i) => setItems(items.filter((_, idx) => idx !== i));
  const setGasto = (k, patch) => setGastos((prev) => prev.map((g, j) => (j === k ? { ...g, ...patch } : g)));

  const totals = useMemo(() => {
    let s15 = 0, s18 = 0, sx = 0, i15 = 0, i18 = 0, td = 0, total = 0;
    items.forEach((it) => {
      const bruto = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0);
      const neto = bruto - (Number(it.descuento) || 0);
      td += Number(it.descuento) || 0;
      if (Number(it.isv_pct) === 15) { s15 += neto; i15 += neto * 0.15; total += neto * 1.15; }
      else if (Number(it.isv_pct) === 18) { s18 += neto; i18 += neto * 0.18; total += neto * 1.18; }
      else { sx += neto; total += neto; }
    });
    return { s15, s18, sx, i15, i18, td, total };
  }, [items]);

  const itemPayload = useMemo(() => items.map((it) => ({
    ...it, cantidad: Number(it.cantidad), precio_unitario: Number(it.precio_unitario),
    descuento: Number(it.descuento) || 0, isv_pct: Number(it.isv_pct), peso_kg: Number(it.peso_kg) || 0,
    batch: it.batch || "", lote_fabricacion: it.lote_fabricacion || "",
    fecha_fabricacion: it.fecha_fabricacion || null,
    fecha_vencimiento: it.fecha_vencimiento || null,
    fecha_test: it.fecha_test || null,
    fecha_ingreso: it.fecha_ingreso || null,
    bodega_id: it.bodega_id || null,
  })), [items]);
  const gastosPayload = useMemo(() => gastos.map((g) => ({
    ...g, monto: Number(g.monto) || 0, tipo_cambio: g.moneda === "HNL" ? 1 : Number(g.tipo_cambio) || 0, isv_monto: Number(g.isv_monto) || 0,
    cuenta_pago_id: g.forma_pago === "contado" ? g.cuenta_pago_id || null : null,
  })), [gastos]);
  const facturaPayload = useMemo(() => ({
    moneda, tipo_cambio: moneda === "USD" ? Number(tasa) || null : null, subcuenta_id: subcuentaId || null,
    proveedor_id: proveedorId, numero_proveedor: numeroProv || "—", cai_proveedor: caiProv, forma_pago: formaPago, fecha,
    dias_credito: formaPago === "credito" ? diasEfectivos : null,
    fecha_vencimiento: formaPago === "credito" && fechaVenc ? fechaVenc : null,
    cuenta_pago_id: formaPago === "contado" ? cuentaPago || null : null,
    // Con OC vinculada, lo ya recibido NO vuelve a mover inventario (el backend solo ingresa lo no recibido)
    purchase_order_id: po ? po.id : null, items: itemPayload, actualizar_inventario: !po, notas,
    tipo_compra: tipoCompra || null, flete_tratamiento: fleteTrat || null, base_prorrateo: base || null,
    gastos: tipoCompra ? gastosPayload : [], expediente: tipoCompra === "importacion" ? expediente : "",
    crear_auxiliar: false,
  }), [proveedorId, numeroProv, caiProv, formaPago, fecha, diasEfectivos, fechaVenc, cuentaPago, po, itemPayload, notas,
    tipoCompra, fleteTrat, base, gastosPayload, expediente, moneda, tasa, subcuentaId]);

  // Vista previa del costo (no guarda nada): se recalcula al cambiar líneas, flete, gastos o base
  useEffect(() => {
    if (mode !== "invoice" || !proveedorId || !tipoCompra || (requiereSub && !subcuentaId) || !items.length || items.some((it) => !it.producto_id || !(Number(it.cantidad) > 0))
        || gastos.some((g) => !String(g.concepto || "").trim() || !(Number(g.monto) > 0))) {
      setPreview(null); setPreviewErr(""); return undefined;
    }
    const t = setTimeout(() => {
      // al editar, el número propio no cuenta como duplicado: la vista previa usa uno provisional
      api.post("/purchase-invoices/preview", { ...facturaPayload, numero_proveedor: (!editando && numeroProv) || `VISTA-PREVIA-${idemKey.slice(0, 8)}` })
        .then(({ data }) => { setPreview(data); setPreviewErr(""); })
        .catch((e) => { setPreview(null); setPreviewErr(apiErr(e)); });
    }, 300);
    return () => clearTimeout(t);
  }, [mode, proveedorId, tipoCompra, items, gastos, facturaPayload, numeroProv, idemKey, editando, requiereSub, subcuentaId]);

  const save = async () => {
    if (!proveedorId) return toast.error("Selecciona un proveedor");
    if (items.length === 0) return toast.error("Agrega al menos una línea");
    if (items.some((it) => !it.producto_id || Number(it.cantidad) <= 0)) return toast.error("Revisa los productos y cantidades");
    if (mode === "invoice") {
      if (moneda === "USD" && !(Number(tasa) > 0)) return toast.error("Factura en dólares: indica la tasa de cambio (lempiras por cada dólar)");
      if (!tipoCompra) return toast.error("Indica el tipo de compra: Compra nacional o Importación");
      if (requiereSub && !subcuentaId) return toast.error("Selecciona la subcuenta del proveedor (país de procedencia)");
      if (!numeroProv) return toast.error("Indica el número de factura del proveedor");
      if (previewErr) return toast.error(previewErr);
      if (editando) {
        if (!esAdmin) return toast.error("Solo el Administrador General puede editar facturas de compra");
        if (!motivo.trim()) return toast.error("Indica el motivo de la edición");
      } else {
        if (!currentBranch) return toast.error("Selecciona la sucursal activa (arriba, junto a la empresa) antes de registrar la compra");
        if (formaPago === "contado" && !cuentaPago) return toast.error("Selecciona la cuenta de tesorería de pago");
        if (!aux?.auxiliar) return toast.error("Crea el auxiliar contable del proveedor en esta sucursal (recuadro Cuenta por pagar)");
      }
    }
    setSaving(true);
    try {
      if (mode === "order") {
        await api.post("/purchase-orders", {
          proveedor_id: proveedorId, forma_pago: formaPago,
          dias_credito: formaPago === "credito" && diasCredito !== "" ? Number(diasCredito) : null,
          fecha_entrega_estimada: fechaEntrega ? new Date(fechaEntrega).toISOString() : null,
          items: itemPayload, notas,
        });
        toast.success("Orden de compra creada");
      } else if (editando) {
        const { data: fac } = await api.put(`/purchase-invoices/${editId}`, { ...facturaPayload, motivo });
        const ult = (fac.ediciones || []).slice(-1)[0];
        toast.success(`Factura ${fac.numero_interno} actualizada${ult?.tipo === "correccion_completa" ? " · inventario, CxP y asiento ajustados" : ""}`);
      } else {
        const { data: fac } = await api.post("/purchase-invoices", { ...facturaPayload, idempotency_key: idemKey });
        // Actualizar precios de venta si el usuario los marcó por línea
        for (const it of items) {
          if (it.update_prices) {
            const upd = {};
            ["p0", "p1", "p2", "p3", "p4"].forEach((k) => {
              const val = it[`new_${k}`];
              if (val !== "" && val !== undefined && val !== null && !isNaN(Number(val))) upd[k] = Number(val);
            });
            if (Object.keys(upd).length && it.producto_id) {
              try { await api.patch(`/products/${it.producto_id}/prices`, upd); } catch (_) { /* silent */ }
            }
          }
        }
        toast.success(`Factura ${fac.numero_interno} registrada · inventario, ${formaPago === "credito" ? "CxP" : "pago"} y asiento en ${fac.branch_nombre || "la sucursal"}`);
      }
      navigate("/compras");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  // Reparto calculado al instante en el formulario (misma fórmula del servidor); la vista previa del servidor lo
  // confirma cuando la compra está completa y es la que se registra.
  const repartoLocal = useMemo(() => {
    if (mode !== "invoice") return null;
    const tc = moneda === "USD" ? Number(tasa) || 0 : 1;
    if (!(tc > 0)) return null;
    const lineas = items.map((it, i) => {
      const p = prodDe(it.producto_id);
      const cantidad = Number(it.cantidad) || 0;
      const valor = r2(((cantidad * (Number(it.precio_unitario) || 0)) - (Number(it.descuento) || 0)) * tc);
      const servicio = Boolean(p && (p.tipo === "servicio" || p.tipo === "no_inventariable" || p.es_flete));
      return { i, codigo: p?.codigo || it.codigo, descripcion: it.descripcion || p?.nombre || "", cantidad, valor, servicio,
        flete: Boolean(p?.es_flete), ingresa: Boolean(p) && !servicio && cantidad > 0, peso: (Number(it.peso_kg) || 0) * cantidad,
        precio_unitario_moneda: Number(it.precio_unitario) || 0, moneda, flete_asignado: 0, otros_asignado: 0 };
    });
    const prod = lineas.filter((l) => l.ingresa);
    if (!prod.length) return null;
    const fuentes = [];
    const flete = r2(lineas.filter((l) => l.flete).reduce((s, l) => s + l.valor, 0));
    if (flete > 0 && (fleteTrat || "costo") === "costo") fuentes.push({ k: "flete_asignado", monto: flete });
    gastos.forEach((g) => {
      const m = Number(g.monto) || 0;
      const t = g.moneda === "HNL" ? 1 : Number(g.tipo_cambio) || 0;
      if (m > 0 && t > 0 && (g.incorporable || g.forma_pago === "solo_costo")) fuentes.push({ k: "otros_asignado", monto: r2(m * t) });
    });
    const bases = prod.map((l) => (base === "cantidad" ? l.cantidad : base === "peso" ? l.peso : l.valor));
    fuentes.forEach((f) => { prorratear(bases, f.monto).forEach((m, j) => { prod[j][f.k] = r2(prod[j][f.k] + m); }); });
    const valorProd = r2(prod.reduce((s, l) => s + l.valor, 0));
    prod.forEach((l) => {
      l.asignado = r2(l.flete_asignado + l.otros_asignado);
      l.precio_unitario_neto = l.valor / l.cantidad;
      l.gasto_unitario = l.asignado / l.cantidad;
      l.costo_unitario_final = (l.valor + l.asignado) / l.cantidad;
      l.costo_total_final = r2(l.valor + l.asignado);
      l.participacion = valorProd ? l.valor / valorProd : 0;
    });
    const total = r2(fuentes.reduce((s, f) => s + f.monto, 0));
    const suma = r2(prod.reduce((s, l) => s + l.asignado, 0));
    return { local: true, lineas: prod, valor_productos: valorProd, total_distribuido: total, suma_asignada: suma,
      cuadra: Math.abs(total - suma) < 0.005,
      flete_distribuido: r2(prod.reduce((s, l) => s + l.flete_asignado, 0)), otros_distribuidos: r2(prod.reduce((s, l) => s + l.otros_asignado, 0)),
      costo_inventario: r2(prod.reduce((s, l) => s + l.costo_total_final, 0)), base_descripcion: BASE_TXT[base] || BASE_TXT.valor,
      moneda, tipo_cambio: tc, sin_base: base === "peso" && bases.some((b) => !(b > 0)) };
  }, [mode, items, moneda, tasa, gastos, fleteTrat, base, products]); // eslint-disable-line react-hooks/exhaustive-deps
  // Qué falta para confirmar el reparto con el servidor (y registrar la compra)
  const faltantes = [
    !proveedorId && "el proveedor",
    !tipoCompra && "el tipo de compra (nacional o importación)",
    requiereSub && !subcuentaId && "la subcuenta del proveedor (país de procedencia)",
    moneda === "USD" && !(Number(tasa) > 0) && "la tasa de cambio",
    items.some((it) => !it.producto_id) && "el producto de cada línea",
    items.some((it) => it.producto_id && !(Number(it.cantidad) > 0)) && "la cantidad de cada línea",
    gastos.some((g) => !String(g.concepto || "").trim() || !(Number(g.monto) > 0)) && "el concepto e importe de cada gasto",
  ].filter(Boolean);
  const rep = preview || repartoLocal;
  const lineaPrev = (i) => rep?.lineas?.find((l) => l.i === i);
  const sel = "w-full bg-app border border-border rounded-md px-3 py-2 text-sm";
  return (
    <Layout
      title={mode === "order" ? "Nueva orden de compra" : editando ? `Editar factura ${original?.numero_interno || ""}` : "Nueva factura de proveedor"}
      subtitle={mode === "order" ? "Solicita mercancía a proveedor (borrador → aprobar → recibir)"
        : editando ? "Solo Administrador General · los cambios de importes ajustan inventario, CxP y asiento con historial"
          : "Registra la factura: inventario (PEPS), cuenta por pagar o pago y asiento en un solo paso"}
      actions={
        <div className="flex gap-2">
          <button onClick={() => navigate("/compras")} className="px-3 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary flex items-center gap-2">
            <ArrowLeftIcon size={14} /> Volver
          </button>
          <button onClick={save} disabled={saving} data-testid="save-purchase-btn" className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-5 py-2 text-sm disabled:opacity-50">
            {saving ? "Guardando…" : mode === "order" ? "Crear OC" : editando ? "Guardar cambios" : "Confirmar compra"}
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {editando && (
            <div className={`rounded-lg p-4 border text-sm ${esAdmin ? "border-agro-yellow/40 bg-agro-yellow/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="purchase-edicion">
              {!esAdmin ? <div className="text-red-300">Solo el Administrador General puede editar facturas de compra.</div> : <>
                <div className="text-xs text-text-secondary mb-2">
                  Editas la factura <b>{original?.numero_interno}</b>. Cambiar número, CAI, fechas o notas solo actualiza el documento.
                  Cambiar líneas, importes, moneda, tasa o gastos reversa y vuelve a registrar inventario, cuenta por pagar y asiento con el mismo número;
                  los pagos aplicados se conservan. Queda registrado quién, cuándo, el motivo y los valores anteriores y nuevos.
                  {original?.pagos?.length > 0 && <b className="block mt-1 text-agro-yellow">Tiene {original.pagos.length} pago(s): no se pueden cambiar la moneda, la tasa, el proveedor ni la forma de pago.</b>}
                </div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Motivo de la edición *</label>
                <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. número de factura mal digitado" data-testid="purchase-motivo"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
              </>}
            </div>
          )}
          {mode === "invoice" && (
            <div className="bg-card border border-border/60 rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid="purchase-contexto">
              <div className="flex items-center gap-2 text-sm" data-testid="purchase-sucursal">
                <BuildingsIcon size={18} className="text-agro-green" />
                <span className="text-text-muted text-xs uppercase tracking-wider">Sucursal</span>
                {editando ? <b>{original?.branch_codigo || "—"} · {original?.branch_nombre || ""}</b>
                  : currentBranch ? <b>{currentBranch.codigo} · {currentBranch.nombre}</b>
                  : <span className="text-red-300 flex items-center gap-1"><WarningCircleIcon size={14} /> Selecciona la sucursal activa</span>}
              </div>
              <div className="flex items-center gap-2 ml-auto" role="radiogroup" data-testid="purchase-tipo">
                <span className="text-text-muted text-xs uppercase tracking-wider">Tipo de compra *</span>
                {[["nacional", "Compra nacional", TruckIcon], ["importacion", "Importación", GlobeIcon]].map(([k, l, Icon]) => (
                  <button key={k} type="button" role="radio" aria-checked={tipoCompra === k} onClick={() => setTipoCompra(k)} data-testid={`purchase-tipo-${k}`}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md border flex items-center gap-1.5 ${tipoCompra === k ? "bg-agro-green text-black border-agro-green" : "border-border text-text-secondary hover:text-text-primary"}`}>
                    <Icon size={14} /> {l}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-card border border-border/60 rounded-lg p-5">
            <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted mb-4">Datos generales</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Lbl>Proveedor *</Lbl>
                {po && (
                  <div className="mb-2 text-xs bg-agro-green/10 border border-agro-green/30 rounded-md p-2 text-text-secondary" data-testid="purchase-po-banner">
                    Factura de la OC <b className="text-agro-green">{po.numero}</b>: se factura lo recibido y pendiente de facturar.
                    La mercadería ya ingresó al inventario en la recepción, por lo que esta factura <b>no vuelve a mover inventario</b>.
                  </div>
                )}
                {/* La factura de una OC conserva el proveedor de la orden (no se cambia) */}
                <SupplierSearch suppliers={suppliers} estado={provEstado} onRetry={cargarProveedores}
                  value={proveedorId} onPick={(id) => setProveedorId(id)} disabled={!!po}
                  seleccionado={po ? { nombre: po.proveedor_nombre, rtn: po.proveedor_rtn, codigo: po.proveedor_codigo } : null}
                  testid="purchase-proveedor" />
                {proveedor && <div className="mt-2 text-xs text-text-muted">RTN: {proveedor.rtn || "—"} · Crédito {proveedor.dias_credito}d · Saldo actual {fmtL(proveedor.saldo)}</div>}
                {mode === "invoice" && proveedorId && subcuentas !== null && (
                  <div className="mt-3 rounded-md border border-border p-3 text-xs" data-testid="purchase-subcuenta">
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="flex-1 min-w-[240px]">
                        <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1 block">
                          Subcuenta por país de procedencia {requiereSub ? "*" : "(opcional)"}</label>
                        {requiereSub ? (
                          <select value={subcuentaId} onChange={(e) => setSubcuentaId(e.target.value)} data-testid="purchase-subcuenta-select"
                            className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs">
                            <option value="">— Selecciona la subcuenta —</option>
                            {subcuentas.map((x) => <option key={x.id} value={x.id}>{x.codigo} · {x.nombre}</option>)}
                          </select>
                        ) : <div className="text-text-muted">Este proveedor no está dividido por país de procedencia.</div>}
                      </div>
                      {!nuevaSub && !editando && (
                        <button type="button" onClick={abrirNuevaSub} data-testid="purchase-subcuenta-nueva"
                          className="px-3 py-1.5 rounded-md border border-agro-green/40 text-agro-green hover:bg-agro-green/10 flex items-center gap-1">
                          <PlusIcon size={12} /> Agregar nueva subcuenta</button>
                      )}
                    </div>
                    {subSel && <div className="mt-1 text-text-muted">Cuenta de control {subSel.cuenta_control} · {subSel.facturas || 0} factura(s) en esta subcuenta</div>}
                    {nuevaSub && (
                      <div className="mt-3 border-t border-border pt-3 space-y-2" data-testid="purchase-subcuenta-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1 block">País de procedencia *</label>
                            <select value={nuevaSub.pais} onChange={(e) => setNuevaSub({ ...nuevaSub, pais: e.target.value })} data-testid="purchase-subcuenta-pais"
                              className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs">
                              <option value="">— Selecciona —</option>
                              {paises.map((x) => <option key={x.codigo} value={x.codigo}>{x.nombre}{subDelPais(x.codigo) ? " (ya existe)" : ""}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1 block">Cuenta de control CxP</label>
                            <select value={nuevaSub.cuenta} onChange={(e) => setNuevaSub({ ...nuevaSub, cuenta: e.target.value })} data-testid="purchase-subcuenta-cuenta"
                              className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs">
                              <option value="">Automática (proveedores del exterior / nacionales)</option>
                              {cuentasCxp.map((c) => <option key={c.codigo} value={c.codigo}>{c.codigo} · {c.nombre}</option>)}
                            </select>
                          </div>
                        </div>
                        {nuevaSub.pais && (subDelPais(nuevaSub.pais)
                          ? <div className="text-agro-yellow" data-testid="purchase-subcuenta-existe">Ya existe {subDelPais(nuevaSub.pais).nombre} ({subDelPais(nuevaSub.pais).codigo}): se seleccionará.</div>
                          : <div className="text-text-secondary" data-testid="purchase-subcuenta-vista">Se creará <b>{proveedor?.nombre} – {paises.find((x) => x.codigo === nuevaSub.pais)?.nombre}</b> con
                            código <b className="font-mono">{proveedor?.codigo}-{nuevaSub.pais}</b> y su auxiliar de CxP en {currentBranch?.nombre || "la sucursal activa"}.</div>)}
                        <div className="flex gap-2 justify-end">
                          <button type="button" onClick={() => setNuevaSub(null)} className="px-3 py-1.5 border border-border rounded-md">Cancelar</button>
                          <button type="button" onClick={crearSubcuenta} disabled={creandoSub || !nuevaSub.pais} data-testid="purchase-subcuenta-guardar"
                            className="px-3 py-1.5 bg-agro-green text-black font-semibold rounded-md disabled:opacity-50">
                            {creandoSub ? "Creando…" : subDelPais(nuevaSub.pais) ? "Seleccionar" : "Crear y seleccionar"}</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {mode === "invoice" && proveedorId && aux && (
                  <div className={`mt-3 rounded-md border p-3 text-xs ${aux.auxiliar ? "border-agro-green/40 bg-agro-green/5" : "border-agro-yellow/50 bg-agro-yellow/5"}`} data-testid="purchase-auxiliar">
                    {aux.error ? <span className="text-red-300">{aux.error}</span> : aux.auxiliar ? (
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon size={16} className="text-agro-green" />
                        <span>Cuenta por pagar: <b className="font-mono">{aux.auxiliar.codigo}</b> · {aux.auxiliar.cuenta_nombre} · sucursal {aux.auxiliar.branch_nombre}</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-agro-yellow"><WarningCircleIcon size={16} /> Este proveedor aún no tiene auxiliar contable en {aux.sucursal?.nombre}. Créalo sin salir de la compra:</div>
                        <div className="flex flex-wrap items-center gap-2">
                          <select value={cuentaAux} onChange={(e) => setCuentaAux(e.target.value)} data-testid="purchase-auxiliar-cuenta" className="bg-app border border-border rounded-md px-2 py-1.5 text-xs">
                            {(aux.cuentas_aplicables || []).map((c) => <option key={c.codigo} value={c.codigo}>{c.codigo} · {c.nombre}</option>)}
                          </select>
                          <button type="button" onClick={crearAux} disabled={creandoAux || !cuentaAux} data-testid="purchase-auxiliar-crear"
                            className="px-3 py-1.5 rounded-md bg-agro-green text-black font-semibold disabled:opacity-50">{creandoAux ? "Creando…" : "Crear auxiliar"}</button>
                          <span className="text-text-muted">Solo cuentas de CxP de movimiento.</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {mode === "invoice" && (
                <>
                  <div>
                    <Lbl>Moneda de la factura *</Lbl>
                    <select value={moneda} onChange={(e) => setMoneda(e.target.value)} data-testid="purchase-moneda" className={sel}>
                      <option value="HNL">Lempiras (HNL)</option>
                      <option value="USD">Dólares (USD)</option>
                    </select>
                  </div>
                  <div>
                    {moneda === "USD" ? <>
                      <Lbl>Tasa de cambio (lempiras por US$ 1) *</Lbl>
                      <input type="number" step="0.0001" min="0" value={tasa} onChange={(e) => setTasa(e.target.value)} placeholder="Ej. 24.5000"
                        data-testid="purchase-tasa" className={sel} />
                    </> : <p className="text-[11px] text-text-muted mt-6">Importes en lempiras.</p>}
                  </div>
                </>
              )}
              <div>
                <Lbl>Forma de pago</Lbl>
                <select value={formaPago} onChange={(e) => setFormaPago(e.target.value)} data-testid="purchase-forma-pago" className={sel}>
                  <option value="contado">Contado</option>
                  <option value="credito">Crédito</option>
                </select>
              </div>
              {formaPago === "credito" && (
                <div>
                  <Lbl>Días de crédito</Lbl>
                  <input type="number" min="0" value={diasCredito} placeholder={proveedor ? `${proveedor.dias_credito || 0} (del proveedor)` : "30"}
                         onChange={(e) => setDiasCredito(e.target.value)} data-testid="purchase-dias-credito" className={sel} />
                </div>
              )}
              {mode === "invoice" && formaPago === "contado" && (
                <div>
                  <Lbl>Pagado desde (tesorería) *</Lbl>
                  <select value={cuentaPago} onChange={(e) => setCuentaPago(e.target.value)} data-testid="purchase-cuenta-pago" className={sel}>
                    <option value="">— Selecciona —</option>
                    {treasury.map((t) => <option key={t.id} value={t.id}>{t.codigo} · {t.nombre} ({fmtL(t.saldo_actual)})</option>)}
                  </select>
                </div>
              )}
              {mode === "invoice" && (
                <>
                  <div>
                    <Lbl>Fecha de factura *</Lbl>
                    <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="purchase-fecha" className={sel} />
                  </div>
                  <div>
                    <Lbl>N° factura proveedor *</Lbl>
                    <input data-testid="purchase-numero" value={numeroProv} onChange={(e) => setNumeroProv(e.target.value)} placeholder="000-001-01-00000XXX" className={sel} />
                  </div>
                  <div>
                    <Lbl>CAI del proveedor</Lbl>
                    <input value={caiProv} onChange={(e) => setCaiProv(e.target.value)} className={`${sel} font-mono`} />
                  </div>
                  {formaPago === "credito" && <div>
                    <Lbl>Fecha vencimiento</Lbl>
                    <input type="date" value={fechaVenc || vencCalculado} onChange={(e) => setFechaVenc(e.target.value)} data-testid="purchase-fecha-venc" className={sel} />
                    {!fechaVenc && <p className="text-[10px] text-text-muted mt-1">Fecha + {diasEfectivos} días (editable)</p>}
                  </div>}
                </>
              )}
              {mode === "order" && (
                <div>
                  <Lbl>Fecha entrega estimada</Lbl>
                  <input type="date" value={fechaEntrega} onChange={(e) => setFechaEntrega(e.target.value)} className={sel} />
                </div>
              )}
            </div>
          </div>

          <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
            <div className="p-5 border-b border-border/60 flex justify-between">
              <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted">Productos y servicios</h3>
              <button onClick={addRow} data-testid="add-purchase-item" className="text-xs bg-agro-green/10 text-agro-green border border-agro-green/30 rounded-md px-3 py-1.5 flex items-center gap-1.5 hover:bg-agro-green/20">
                <PlusIcon size={12} weight="bold" /> Agregar línea
              </button>
            </div>
            {mode === "invoice" && (
              <div className="px-5 pt-4 pb-2 text-xs text-text-secondary">
                Escribe parte del nombre o código del producto y elige con el mouse o con ↑ ↓ y Enter. Para el flete del proveedor agrega el ítem <b>Flete</b> (servicio: no genera existencias).
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-text-muted text-left">
                    <th className="p-2 pl-5">Producto</th>
                    <th className="p-2 text-right w-20">Cant.</th>
                    <th className="p-2 text-right w-28">Costo ({moneda === "USD" ? "US$" : "L."})</th>
                    <th className="p-2 text-right w-20">Desc.</th>
                    <th className="p-2 w-20">ISV</th>
                    <th className="p-2 text-right w-28">Total ({moneda === "USD" ? "US$" : "L."})</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const bruto = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0);
                    const neto = bruto - (Number(it.descuento) || 0);
                    const linea = neto * (1 + Number(it.isv_pct) / 100);
                    const srv = esServicio(it);
                    const showTrace = mode === "invoice" && !!it.producto_id && !srv;
                    const prod = prodDe(it.producto_id);
                    const lp = lineaPrev(i);
                    return (
                      <Fragment key={i}>
                        <tr key={`main-${i}`} className="border-t border-border/40">
                          <td className="p-2 pl-5 min-w-[260px]">
                            <ProductSearch products={products} value={it.producto_id} onPick={(pid) => pickProduct(i, pid)} testid={`prow-product-${i}`}
                              placeholder="Escribe nombre o código…" autoFocus={!it.producto_id && i === items.length - 1} />
                            {srv && <div className="text-[10px] text-agro-yellow mt-1" data-testid={`prow-servicio-${i}`}>Servicio · no genera existencias{prod?.es_flete ? ` · ${fleteTrat === "gasto" ? "se registra como gasto" : "se distribuye al costo de los productos"}` : ""}</div>}
                            {lp?.ingresa && lp.asignado > 0 && <div className="text-[10px] text-text-muted mt-1">+ {fmtL(lp.asignado)} de flete / gastos · costo unitario final {fmtL(lp.costo_unitario_final)}</div>}
                          </td>
                          <td className="p-2"><input type="number" data-testid={`prow-qty-${i}`} ref={(el) => { qtyRefs.current[i] = el; }} value={it.cantidad}
                            onChange={(e) => updateRow(i, { cantidad: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                          <td className="p-2"><input type="number" data-testid={`prow-price-${i}`} value={it.precio_unitario} onChange={(e) => updateRow(i, { precio_unitario: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                          <td className="p-2"><input type="number" value={it.descuento} onChange={(e) => updateRow(i, { descuento: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                          <td className="p-2"><select value={it.isv_pct} onChange={(e) => updateRow(i, { isv_pct: e.target.value })} className="w-full bg-app border border-border rounded px-1 py-1.5 text-xs"><option value={0}>0%</option><option value={15}>15%</option><option value={18}>18%</option></select></td>
                          <td className="p-2 text-right text-xs font-medium text-agro-green">{fmtMon(linea, moneda)}</td>
                          <td className="p-2"><button aria-label="Eliminar" onClick={() => removeRow(i)} className="text-text-muted hover:text-red-400"><TrashIcon size={14} /></button></td>
                        </tr>
                        {showTrace && (
                          <tr key={`trace-${i}`} className="bg-white/[0.02]" data-testid={`prow-trace-${i}`}>
                            <td colSpan="7" className="pl-5 pr-5 pb-3 pt-1">
                              <div className="border-l-2 border-agro-yellow/40 pl-3 py-1">
                                <div className="text-[10px] uppercase tracking-wider text-agro-yellow font-medium mb-2 flex items-center gap-1.5">
                                  <span className="inline-flex items-center gap-1"><PackageIcon size={12} aria-hidden="true" /> Trazabilidad del lote {it.es_semilla && <span className="text-agro-green inline-flex items-center gap-0.5"><PlantIcon size={12} aria-hidden="true" /> (semilla)</span>}</span>
                                </div>
                                <div className="mb-3">
                                  <label className="text-[10px] uppercase tracking-wider text-agro-yellow/80 font-medium mb-1 block">
                                    Descripción del producto <span className="text-text-muted normal-case">(aparece en la factura de venta)</span>
                                  </label>
                                  <input type="text" value={it.descripcion || ""} onChange={(e) => updateRow(i, { descripcion: e.target.value })}
                                         placeholder="Ej. Urea Granulada 46% N — saco 50kg"
                                         className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-yellow" data-testid={`prow-descripcion-${i}`} />
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                                  <InlineField label="Fecha de ingreso" hint="Ingreso físico: determina el orden PEPS (la fecha de facturación es la de la factura)">
                                    <input type="date" value={it.fecha_ingreso || ""} onChange={(e) => updateRow(i, { fecha_ingreso: e.target.value })}
                                           className="w-full bg-app border border-border rounded px-2 py-1 text-xs focus:border-agro-yellow" data-testid={`prow-fecha-ingreso-${i}`} />
                                  </InlineField>
                                  <InlineField label="Batch">
                                    <input type="text" value={it.batch || ""} placeholder="B-001" onChange={(e) => updateRow(i, { batch: e.target.value })}
                                           className="w-full bg-app border border-border rounded px-2 py-1 text-xs focus:border-agro-yellow" data-testid={`prow-batch-${i}`} />
                                  </InlineField>
                                  <InlineField label="Lote fabricación">
                                    <input type="text" value={it.lote_fabricacion || ""} placeholder="LF-2026-001" onChange={(e) => updateRow(i, { lote_fabricacion: e.target.value })}
                                           className="w-full bg-app border border-border rounded px-2 py-1 text-xs focus:border-agro-yellow" data-testid={`prow-lote-fab-${i}`} />
                                  </InlineField>
                                  {it.es_semilla ? (
                                    <InlineField label="Fecha test">
                                      <input type="date" value={it.fecha_test || ""} onChange={(e) => updateRow(i, { fecha_test: e.target.value })}
                                             className="w-full bg-app border border-border rounded px-2 py-1 text-xs focus:border-agro-yellow" data-testid={`prow-test-${i}`} />
                                    </InlineField>
                                  ) : (
                                    <>
                                      <InlineField label="Fecha fabricación">
                                        <input type="date" value={it.fecha_fabricacion || ""} onChange={(e) => updateRow(i, { fecha_fabricacion: e.target.value })}
                                               className="w-full bg-app border border-border rounded px-2 py-1 text-xs focus:border-agro-yellow" data-testid={`prow-fab-${i}`} />
                                      </InlineField>
                                      <InlineField label="Fecha vencimiento">
                                        <input type="date" value={it.fecha_vencimiento || ""} onChange={(e) => updateRow(i, { fecha_vencimiento: e.target.value })}
                                               className="w-full bg-app border border-border rounded px-2 py-1 text-xs focus:border-agro-yellow" data-testid={`prow-venc-${i}`} />
                                      </InlineField>
                                    </>
                                  )}
                                  <InlineField label="Bodega destino">
                                    <select value={it.bodega_id || ""} onChange={(e) => updateRow(i, { bodega_id: e.target.value })}
                                            className="w-full bg-app border border-border rounded px-1 py-1 text-xs focus:border-agro-yellow" data-testid={`prow-bodega-${i}`}>
                                      <option value="">—</option>
                                      {warehouses.map((w) => <option key={w.id} value={w.id}>{w.codigo}</option>)}
                                    </select>
                                  </InlineField>
                                  {base === "peso" && (
                                    <InlineField label="Peso (kg / unidad)" hint="Base de prorrateo por peso">
                                      <input type="number" min="0" step="any" value={it.peso_kg ?? ""} onChange={(e) => updateRow(i, { peso_kg: e.target.value })}
                                             className="w-full bg-app border border-border rounded px-2 py-1 text-xs focus:border-agro-yellow" data-testid={`prow-peso-${i}`} />
                                    </InlineField>
                                  )}
                                </div>

                                {/* Actualizar precios de venta */}
                                <div className="mt-3 pt-2 border-t border-white/[0.04]">
                                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid={`prow-price-toggle-${i}`}>
                                    <input type="checkbox" checked={!!it.update_prices}
                                           onChange={(e) => updateRow(i, {
                                             update_prices: e.target.checked,
                                             new_p0: e.target.checked && !it.new_p0 ? (prod?.precio_p0 ?? "") : it.new_p0,
                                             new_p1: e.target.checked && !it.new_p1 ? (prod?.precio_p1 ?? "") : it.new_p1,
                                             new_p2: e.target.checked && !it.new_p2 ? (prod?.precio_p2 ?? "") : it.new_p2,
                                             new_p3: e.target.checked && !it.new_p3 ? (prod?.precio_p3 ?? "") : it.new_p3,
                                             new_p4: e.target.checked && !it.new_p4 ? (prod?.precio_p4 ?? "") : it.new_p4,
                                           })}
                                           className="w-3.5 h-3.5 accent-agro-green" />
                                    <span className="text-agro-green font-medium">Actualizar precio de venta desde este ingreso</span>
                                    {prod && <span className="text-[10px] text-text-muted">(actuales: P1 {fmtL(prod.precio_p1 || 0)} · P4 {fmtL(prod.precio_p4 || 0)})</span>}
                                  </label>
                                  {it.update_prices && (
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                                      {["p0", "p1", "p2", "p3", "p4"].map((k) => (
                                        <InlineField key={k} label={`Nuevo ${k.toUpperCase()}`}>
                                          <input type="number" min="0" step="any" value={it[`new_${k}`] ?? ""} onChange={(e) => updateRow(i, { [`new_${k}`]: e.target.value })}
                                                 className="w-full bg-app border border-border rounded px-2 py-1 text-xs focus:border-agro-green" data-testid={`prow-new-${k}-${i}`} />
                                        </InlineField>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {items.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-text-muted text-sm">Agrega productos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {mode === "invoice" && tipoCompra && (
            <div className="bg-card border border-border/60 rounded-lg p-5 space-y-3" data-testid="purchase-gastos">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted">
                  {tipoCompra === "importacion" ? "Gastos de nacionalización y otros costos" : "Otros gastos de la compra (facturados por terceros)"}</h3>
                <button type="button" onClick={() => setGastos([...gastos, nuevoGasto()])} data-testid="purchase-gasto-agregar"
                  className="text-xs bg-agro-green/10 text-agro-green border border-agro-green/30 rounded-md px-3 py-1.5 flex items-center gap-1.5 hover:bg-agro-green/20"><PlusIcon size={12} /> Agregar gasto</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {tipoCompra === "importacion" && <div><Lbl>Expediente de importación</Lbl>
                  <input value={expediente} onChange={(e) => setExpediente(e.target.value.toUpperCase())} placeholder="EXP-2026-001 (agrupa varias facturas)" data-testid="purchase-expediente" className={sel} /></div>}
                <div className={`${tipoCompra === "importacion" ? "md:col-span-2" : "md:col-span-3"} text-[11px] text-text-muted self-end`}>
                  El flete que viene en la factura del proveedor se agrega como línea <b>Flete</b>; aquí van los cargos de otros proveedores (transporte, agente, almacenaje…).
                  Cada gasto con proveedor genera su propio documento en la cuenta por pagar de ESE proveedor (no se suma a la deuda del proveedor de la mercadería).
                  Los incorporables se distribuyen al costo; los demás van a su cuenta de gasto.
                </div>
              </div>
              {gastos.length === 0 ? <div className="text-xs text-text-muted">Sin gastos registrados en esta factura. También puedes agregarlos después (ajuste de costo).</div> : gastos.map((g, k) => (
                <div key={k} className="border border-border rounded-md p-3 grid grid-cols-2 md:grid-cols-6 gap-2 text-xs" data-testid={`purchase-gasto-${k}`}>
                  <div className="md:col-span-2"><InlineField label="Concepto *"><input value={g.concepto} onChange={(e) => setGasto(k, { concepto: e.target.value })} placeholder="DAI, agente aduanero, almacenaje…" className="w-full bg-app border border-border rounded px-2 py-1" data-testid={`gasto-concepto-${k}`} /></InlineField></div>
                  <InlineField label="Importe (sin ISV) *"><input type="number" step="any" value={g.monto} onChange={(e) => setGasto(k, { monto: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1 text-right" data-testid={`gasto-monto-${k}`} /></InlineField>
                  <InlineField label="Moneda"><select value={g.moneda} onChange={(e) => setGasto(k, { moneda: e.target.value, tipo_cambio: e.target.value === "HNL" ? "1" : g.tipo_cambio })} className="w-full bg-app border border-border rounded px-1 py-1" data-testid={`gasto-moneda-${k}`}><option>HNL</option><option>USD</option><option>EUR</option></select></InlineField>
                  {g.moneda !== "HNL" ? <InlineField label="Tipo de cambio (L)"><input type="number" step="any" value={g.tipo_cambio} onChange={(e) => setGasto(k, { tipo_cambio: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1 text-right" data-testid={`gasto-tc-${k}`} /></InlineField>
                    : <div className="text-[10px] text-text-muted self-end pb-1">En lempiras</div>}
                  {g.forma_pago !== "solo_costo" && <>
                    <InlineField label="ISV del documento"><input type="number" step="any" value={g.isv_monto} onChange={(e) => setGasto(k, { isv_monto: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1 text-right" /></InlineField>
                    <label className="flex items-center gap-1.5 md:col-span-2"><input type="checkbox" checked={g.incorporable} onChange={(e) => setGasto(k, { incorporable: e.target.checked })} className="accent-agro-green" data-testid={`gasto-incorporable-${k}`} /> Forma parte del costo del inventario</label>
                    {!g.incorporable && <div className="md:col-span-2"><InlineField label="Cuenta de gasto *"><select value={g.cuenta_gasto} onChange={(e) => setGasto(k, { cuenta_gasto: e.target.value })} className="w-full bg-app border border-border rounded px-1 py-1"><option value="">—</option>{cuentasGasto.map((c) => <option key={c.codigo} value={c.codigo}>{c.codigo} · {c.nombre}</option>)}</select></InlineField></div>}
                  </>}
                  <div className={g.forma_pago === "solo_costo" ? "md:col-span-2" : ""}><InlineField label="Forma"><select value={g.forma_pago} onChange={(e) => setGasto(k, { forma_pago: e.target.value, incorporable: e.target.value === "solo_costo" ? true : g.incorporable })} className="w-full bg-app border border-border rounded px-1 py-1" data-testid={`gasto-forma-${k}`}>
                    <option value="solo_costo">Solo para el costo (sin proveedor)</option><option value="credito">Se le debe a un proveedor</option><option value="contado">Pagado de contado</option></select></InlineField></div>
                  {g.forma_pago === "solo_costo" ? (
                    <div className="col-span-2 md:col-span-6 text-[11px] text-agro-yellow" data-testid={`gasto-solo-costo-${k}`}>
                      Se reparte al costo real de los productos de esta compra. No se liga a un proveedor ni genera cuenta por pagar ni asiento:
                      queda <b>pendiente de contabilizar</b> hasta que se cargue el gasto real.
                    </div>
                  ) : g.forma_pago === "credito" ? <>
                    <div className="col-span-2 md:col-span-4"><InlineField label="Proveedor del gasto *"><SupplierSearch suppliers={suppliers} estado={provEstado} value={g.proveedor_id} onPick={(id) => setGasto(k, { proveedor_id: id })} testid={`gasto-proveedor-${k}`} /></InlineField></div>
                    <InlineField label="N° documento *"><input value={g.numero_documento} onChange={(e) => setGasto(k, { numero_documento: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1" data-testid={`gasto-doc-${k}`} /></InlineField>
                  </> : <div className="md:col-span-2"><InlineField label="Pagado desde *"><select value={g.cuenta_pago_id} onChange={(e) => setGasto(k, { cuenta_pago_id: e.target.value })} className="w-full bg-app border border-border rounded px-1 py-1"><option value="">—</option>{treasury.map((t) => <option key={t.id} value={t.id}>{t.codigo} · {t.nombre}</option>)}</select></InlineField></div>}
                  <div className="flex items-end justify-end"><button type="button" onClick={() => setGastos(gastos.filter((_, j) => j !== k))} className="text-text-muted hover:text-red-400" title="Quitar gasto" aria-label="Quitar gasto"><TrashIcon size={14} /></button></div>
                </div>
              ))}
            </div>
          )}

          {mode === "invoice" && (hayFlete || gastos.length > 0 || rep) && (
            <div className="bg-card border border-border/60 rounded-lg p-5 space-y-3" data-testid="purchase-prorrateo">
              <div className="flex flex-wrap items-end gap-3">
                <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted mr-auto">Costo de adquisición · vista previa del reparto</h3>
                {hayFlete && <div><Lbl>Flete</Lbl>
                  <select value={fleteTrat} onChange={(e) => setFleteTrat(e.target.value)} data-testid="purchase-flete-trat" className="bg-app border border-border rounded-md px-2 py-1.5 text-xs">
                    <option value="costo">Distribuir al costo de los productos</option><option value="gasto">Registrar como gasto</option>
                  </select></div>}
                <div><Lbl>Base de prorrateo</Lbl>
                  <select value={base} onChange={(e) => setBase(e.target.value)} data-testid="purchase-base" className="bg-app border border-border rounded-md px-2 py-1.5 text-xs">
                    <option value="valor">Valor de compra de la línea</option><option value="cantidad">Cantidad</option><option value="peso">Peso (kg)</option>
                  </select></div>
              </div>
              {previewErr && <div className="text-xs text-red-300 flex items-center gap-1.5" data-testid="purchase-preview-error"><WarningCircleIcon size={14} /> {previewErr}</div>}
              {!preview && faltantes.length > 0 && (
                <div className="text-xs text-agro-yellow" data-testid="purchase-preview-faltantes">
                  {rep ? "Reparto calculado en el formulario. " : ""}Para confirmarlo y registrar la compra falta: <b>{faltantes.join(", ")}</b>.
                </div>
              )}
              {!rep ? <div className="text-xs text-text-muted">Agrega productos (con cantidad y precio) para ver el costo real unitario.</div> : (() => { const preview = rep; return (
                  <>
                    <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="purchase-preview">
                      <thead className="text-[10px] uppercase text-text-muted"><tr className="text-left">
                        <th className="py-1">Producto · cantidad</th><th className="text-right px-1">Precio unit.</th>
                        <th className="text-right px-1">Valor línea · particip.</th><th className="text-right px-1">Flete</th>
                        <th className="text-right px-1">Otros gastos</th><th className="text-right px-1">Gasto adic. / unidad</th>
                        <th className="text-right px-1">Costo unit. final</th><th className="text-right pl-1">Costo total final</th>
                      </tr></thead>
                      <tbody>
                        {preview.lineas.filter((l) => l.ingresa).map((l) => (
                          <tr key={l.i} className="border-t border-border/40" data-testid={`preview-linea-${l.codigo}`}>
                            <td className="py-1 pr-2">{l.codigo} · {l.descripcion}<div className="text-[10px] text-text-muted">{l.cantidad} unidades</div>{Object.keys(l.desglose || {}).filter((c) => c !== "Flete").length > 1 && <div className="text-[10px] text-text-muted">{Object.entries(l.desglose).filter(([c]) => c !== "Flete").map(([c, m]) => `${c} ${fmtL(m)}`).join(" · ")}</div>}</td>
                            <td className="text-right px-1 whitespace-nowrap">{fmtL(l.precio_unitario_neto)}{l.moneda === "USD" && <div className="text-[10px] text-text-muted">{fmtMon(l.precio_unitario_moneda, "USD")}</div>}</td>
                            <td className="text-right px-1 whitespace-nowrap">{fmtL(l.valor)}<div className="text-[10px] text-text-muted">{(l.participacion * 100).toFixed(2)}%</div></td>
                            <td className="text-right px-1 whitespace-nowrap text-agro-yellow" data-testid={`preview-flete-${l.codigo}`}>{fmtL(l.flete_asignado)}</td>
                            <td className="text-right px-1 whitespace-nowrap text-agro-yellow" data-testid={`preview-otros-${l.codigo}`}>{fmtL(l.otros_asignado)}</td>
                            <td className="text-right px-1 whitespace-nowrap">{fmtL(l.gasto_unitario)}</td>
                            <td className="text-right px-1 whitespace-nowrap font-semibold text-agro-green" data-testid={`preview-costo-${l.codigo}`}>{fmtL(l.costo_unitario_final)}</td>
                            <td className="text-right pl-1 whitespace-nowrap">{fmtL(l.costo_total_final)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr className="border-t border-border font-semibold">
                        <td className="py-1" colSpan="2">Base: {preview.base_descripcion}</td>
                        <td className="text-right px-1 whitespace-nowrap">{fmtL(preview.valor_productos)}</td>
                        <td className="text-right px-1 whitespace-nowrap">{fmtL(preview.flete_distribuido)}</td><td className="text-right px-1 whitespace-nowrap">{fmtL(preview.otros_distribuidos)}</td>
                        <td />
                        <td /><td className="text-right pl-1 whitespace-nowrap" data-testid="purchase-preview-costo-inv">{fmtL(preview.costo_inventario)}</td>
                      </tr></tfoot>
                    </table>
                    </div>
                    <div className={`text-[11px] ${preview.cuadra ? "text-agro-green" : "text-red-300"}`}>
                      Flete y gastos repartidos: <b data-testid="purchase-preview-suma">{fmtL(preview.suma_asignada)} de {fmtL(preview.total_distribuido)}</b>{preview.cuadra ? " · cuadra exacto" : " · revisa la base de prorrateo"}
                    </div>
                    {preview.moneda === "USD" && <div className="text-[11px] text-text-muted">Importes en lempiras: los precios en US$ se convierten con la tasa de la factura ({preview.tipo_cambio}) y cada gasto con la suya antes de repartir.</div>}
                    {preview.sin_base && <div className="text-[11px] text-red-300">Prorrateo por peso: indica el peso (kg por unidad) de cada producto.</div>}
                    {!preview.local && <div className="text-[11px] text-text-muted" data-testid="purchase-preview-resumen">
                      Factura del proveedor (productos{hayFlete ? " + flete" : ""}{preview.totales?.isv_15 + preview.totales?.isv_18 > 0 ? " + ISV" : ""}): <b>{fmtL(preview.total_factura)}</b> ·
                      costo del inventario que ingresa: <b>{fmtL(preview.costo_inventario)}</b> · el flete distribuido no se cobra dos veces.
                      <br />Deuda con el proveedor de la mercadería: <b>{fmtL(preview.deuda_proveedor)}</b>
                      {preview.gastos_otros_documentos > 0 && <> · gastos en documentos de otros proveedores / pagos: <b>{fmtL(preview.gastos_otros_documentos)}</b></>}
                      {preview.costos_solo_costo > 0 && <> · solo para el costo (sin proveedor, pendiente de contabilizar): <b data-testid="purchase-preview-solo-costo">{fmtL(preview.costos_solo_costo)}</b></>}
                      {preview.gastos_no_incorporables > 0 && <> · no incorporables (gasto): <b>{fmtL(preview.gastos_no_incorporables)}</b></>}
                    </div>}
                  </>
                ); })()}
            </div>
          )}

          <div className="bg-card border border-border/60 rounded-lg p-5">
            <Lbl>Notas</Lbl>
            <textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} className={sel} placeholder="Observaciones…" />
          </div>
        </div>

        <div>
          <div className="bg-card border border-border/60 rounded-lg p-5 sticky top-20">
            <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted mb-4">Resumen</h3>
            <div className="space-y-2 text-sm">
              <TR l="Gravado 15%" v={fmtMon(totals.s15, moneda)} />
              <TR l="Gravado 18%" v={fmtMon(totals.s18, moneda)} />
              <TR l="Exento" v={fmtMon(totals.sx, moneda)} />
              <TR l="Descuentos" v={fmtMon(totals.td, moneda)} />
              <div className="h-px bg-border my-2" />
              <TR l="ISV 15%" v={fmtMon(totals.i15, moneda)} />
              <TR l="ISV 18%" v={fmtMon(totals.i18, moneda)} />
              <div className="h-px bg-border my-2" />
              <div className="flex justify-between items-baseline pt-2">
                <span className="text-text-secondary text-xs uppercase tracking-wider">Total factura</span>
                <span className="font-heading text-3xl font-semibold text-agro-green" data-testid="purchase-total">{fmtMon(totals.total, moneda)}</span>
              </div>
              {moneda === "USD" && (
                <div className="bg-app border border-border rounded p-2 text-xs" data-testid="purchase-equivalente">
                  <div className="flex justify-between"><span className="text-text-secondary">Equivalente en lempiras</span>
                    <b>{Number(tasa) > 0 ? fmtL(Math.round(Math.round(totals.total * 100) / 100 * Number(tasa) * 100) / 100) : "—"}</b></div>
                  <div className="text-[10px] text-text-muted">{fmtMon(totals.total, "USD")} × {Number(tasa) > 0 ? tasa : "tasa"} · la CxP, el asiento y el costo PEPS se registran en lempiras</div>
                </div>
              )}
              <div className="text-[11px] text-text-muted mt-2 leading-relaxed">
                {mode === "invoice"
                  ? <>
                    {formaPago === "credito" ? `Se registra en la CxP del proveedor · vence ${fechaVenc || vencCalculado || "—"}` : "Se descuenta de la cuenta de tesorería seleccionada"}
                    {currentBranch && <> · sucursal {currentBranch.nombre}</>}
                    {cfg && <div className="mt-1">Inventario {cfg.cuenta_inventario} · ISV {cfg.cuenta_isv_credito}{aux?.auxiliar && <> · CxP {aux.auxiliar.cuenta_control}</>}</div>}
                  </>
                  : "OC: pasará a borrador hasta su aprobación"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{children}</label>;
const TR = ({ l, v }) => <div className="flex justify-between"><span className="text-text-secondary text-xs">{l}</span><span className="text-text-primary">{v}</span></div>;
const InlineField = ({ label, hint, children }) => (
  <div>
    <label className="text-[9px] uppercase tracking-wider text-text-muted mb-0.5 block" title={hint || ""}>{label}</label>
    {children}
  </div>
);
